<template>
  <div>
    <h3 class="body-1-bold mt-8">{{ i18n(translations.title) }}</h3>
    <p class="body-1 mt-16">
      {{ i18n(translations.paragraph) }}
    </p>
    <div v-if="showSelectedReasons">
      <h2 class="heading-2 mb-12 mt-32">
        {{ i18n(translations.assessmentTitle) }}
      </h2>
      <RevList>
        <RevListItemStatic
          v-for="group in selectedReasonsByGroup"
          :key="group.id"
        >
          <template #label>
            <p class="body-2-bold">
              {{ group.title }}
            </p>
          </template>
          <template #description>
            <p
              v-for="reason in group.reasons"
              :key="reason.id"
              class="body-2 mb-4"
            >
              {{ reason.label }}
            </p>
          </template>
        </RevListItemStatic>
      </RevList>
    </div>

    <RevForm
      :id="formId"
      class="mt-32 grid gap-16"
      :initial-values
      :validate
      @submit="handleSubmit"
    >
      <template #default="{ values, errors }">
        <RevInputFileUpload
          id="files"
          v-model="values.attachments"
          :accept="ALLOWED_FILES_EXTENSIONS"
          data-qa="files"
          :description="productAttachmentInputDescription"
          :errors="getAttachmentsErrors(errors)"
          file-preview-display="grid"
          :label-empty="i18n(translations.fileInputLabel)"
          :max-file-size="MAX_FILE_SIZE"
          :max-files="MAX_FILES_COUNT"
          @click="clearFileErrors"
          @errors="(errors) => handleAttachmentsInputErrors(errors)"
        />
        <RevInputTextarea
          id="comment"
          v-model="values.comment"
          data-qa="comment"
          :error="errors.comment"
          :label="i18n(translations.textInputComment)"
          :maxlength="MESSAGE_COUNTEROFFER_MAX_LENGTH"
        >
          <template #count="{ count }">
            {{ i18n(translations.charactersLeft, { count }) }}
          </template>
        </RevInputTextarea>
        <div class="flex">
          <RevInputText
            id="counterOfferPrice"
            v-model="values.counterOfferPrice"
            class="mr-12"
            data-qa="counterOfferPrice"
            :error="errors.counterOfferPrice"
            :label="i18n(translations.textInputCounterOfferPrice)"
            type="number"
          />
          <RevInputText
            id="price"
            v-model="values.price"
            data-qa="price"
            disabled
            :label="i18n(translations.textInputPrice)"
            type="text"
          />
        </div>
      </template>
    </RevForm>
    <RevInfoBlock
      class="mb-16 mt-32"
      :content="i18n(translations.infoContent)"
      :icon="IconMoney"
      :title="i18n(translations.infoTitle)"
      variant="default"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import type { FileError } from '@ds/components/InputFileUpload/InputFileUpload.type'
import { RevInputText } from '@ds/components/InputText'
import { RevInputTextarea } from '@ds/components/InputTextarea'
import { RevList } from '@ds/components/List'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { IconEyeOpen } from '@ds/icons/IconEyeOpen'
import { IconHeartDiagnostic } from '@ds/icons/IconHeartDiagnostic'
import { IconMoney } from '@ds/icons/IconMoney'

import {
  ALLOWED_FILES_EXTENSIONS,
  ERROR_TYPES,
  MAX_FILES_COUNT,
  MAX_FILE_SIZE,
  MESSAGE_COUNTEROFFER_MAX_LENGTH,
} from '~/constants/buyback'
import type { CounterOfferFormattedReasonsGroup } from '~/scopes/buyback/pages/orderDetails/components/OrderOnHoldModal/counterOfferReasonsGroupsAdapter'

import { ON_HOLD_STEPS } from '../../constants'
import { filterPreselectedReasons } from '../helpers'

import translations from './CounterOfferDetailsView.translations'

const GROUPS_IDS_SUFFIXES = {
  APPEARANCE: 'appearance',
  FUNCTIONAL: 'functional',
}

const INTEGER_PRICE_REGEX = /^[1-9]\d*$/

const props = defineProps<{
  offerPrice: string
  reasonsGroups: CounterOfferFormattedReasonsGroup[]
}>()

const emit = defineEmits<{
  (
    e: 'submit',
    value: {
      price: string
      counterOfferPrice: string
      attachments: File[]
      comment: string
      reasons: string[]
    },
  ): void
}>()

const i18n = useI18n()

const initialValues = ref<{
  price: string
  counterOfferPrice: string
  attachments: File[]
  comment: string
}>({
  price: props.offerPrice,
  counterOfferPrice: '',
  attachments: [],
  comment: '',
})

const attachmentsInputErrors = ref<FileError[]>([])
const attachmentsInputErrorMessage = ref('')

const formId = ON_HOLD_STEPS.MAKE_COUNTER_OFFER_DETAILS

const validate = computed(() => {
  const requiredErrorMessage = i18n(translations.required)
  const priceFormatError = i18n(translations.textInputCounterOfferPriceError)

  return makeValidate<typeof initialValues.value>({
    attachments: [required(requiredErrorMessage)],
    counterOfferPrice: [
      required(requiredErrorMessage),
      matchingRegExp(INTEGER_PRICE_REGEX, priceFormatError),
    ],
  })
})

const productAttachmentInputDescription = computed(() => {
  if (attachmentsInputErrorMessage.value !== '') {
    return attachmentsInputErrorMessage.value
  }

  return i18n(translations.fileInputDescription)
})

const inputFileErrorsLabel = computed(() => ({
  [ERROR_TYPES.MAX_FILE_SIZE_EXCEEDED]: i18n(translations.fileTooLargeError),
  [ERROR_TYPES.MAX_FILES_EXCEEDED]: i18n(translations.tooManyFilesError),
}))

function getStaticContent(id: string) {
  if (id.includes(GROUPS_IDS_SUFFIXES.APPEARANCE)) {
    return {
      title: i18n(translations.appearanceTitle),
      icon: IconEyeOpen,
    }
  }

  if (id.includes(GROUPS_IDS_SUFFIXES.FUNCTIONAL)) {
    return {
      title: i18n(translations.functionalTitle),
      icon: IconHeartDiagnostic,
    }
  }

  return {}
}

const selectedReasonsByGroup = computed(() => {
  return props.reasonsGroups
    .map((group) => {
      return {
        id: group.id,
        reasons: group.reasons.filter(({ checked }) => checked === true),
        ...getStaticContent(group.id),
      }
    })
    .filter((group) => !isEmpty(group.reasons))
})

const showSelectedReasons = computed(
  () => !isEmpty(selectedReasonsByGroup.value),
)

function clearFileErrors() {
  attachmentsInputErrors.value = []
  attachmentsInputErrorMessage.value = ''
}

function handleAttachmentsInputErrors(errors: FileError[]) {
  attachmentsInputErrors.value = errors
  attachmentsInputErrorMessage.value = errors.reduce(
    (_, error) => inputFileErrorsLabel.value[error.type],
    '',
  )
}

function getAttachmentsErrors(
  errors: Partial<
    Record<'price' | 'counterOfferPrice' | 'attachments' | 'comment', string>
  >,
): FileError[] | undefined {
  if (!isEmpty(attachmentsInputErrors.value)) {
    return attachmentsInputErrors.value
  }

  if (isEmpty(initialValues.value.attachments) && errors.attachments) {
    attachmentsInputErrorMessage.value = errors.attachments

    return [
      {
        type: 'WRONG_FILE_EXTENSION',
        meta: { filename: 'Unknown' },
      },
    ]
  }

  return undefined
}

function handleSubmit(values: {
  price: string
  counterOfferPrice: string
  attachments: File[]
  comment: string
}) {
  emit('submit', {
    ...values,
    reasons: filterPreselectedReasons(props.reasonsGroups),
  })
}
</script>
